import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import cx from 'classnames';

import { useField } from '../field';

export type ChoiceSize = 'sm' | 'md';

/** The box of `Checkbox` and the circle of `Radio`: it sits after a `peer` input and follows its state. */
export const choiceBoxClasses = (size: ChoiceSize, invalid: boolean, disabled?: boolean) =>
  cx(
    // `mt-0.5` centres the box on the first line of the label at both sizes.
    'mt-0.5 flex shrink-0 items-center justify-center border text-transparent transition-colors',
    size === 'sm' ? 'h-4 w-4' : 'h-5 w-5',
    'peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2',
    disabled
      ? 'border-line-strong bg-surface-muted'
      : cx(
          'bg-surface',
          invalid
            ? 'border-danger'
            : 'border-line-strong group-hover/choice:border-primary peer-checked:border-primary',
        ),
  );

export const choiceLabelClasses = (size: ChoiceSize, disabled?: boolean) =>
  cx(
    'group/choice inline-flex items-start',
    size === 'sm' ? 'gap-2.5 text-ui' : 'gap-3 text-body-l',
    disabled ? 'cursor-not-allowed text-ink-subtle' : 'cursor-pointer text-ink',
  );

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  /** `sm` for consents and lists, `md` beside 16px text. */
  size?: ChoiceSize;
  /** Marks the box as wrong; inside a `Field` with an error it is marked already. */
  invalid?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, size = 'md', invalid, className, disabled, ...rest }, ref) => {
    const field = useField();
    const isInvalid = Boolean(invalid || field?.invalid);

    return (
      <label className={cx(choiceLabelClasses(size, disabled), className)}>
        <input
          ref={ref}
          type="checkbox"
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={field?.describedBy}
          className="peer sr-only"
          {...rest}
        />
        <span
          aria-hidden
          className={cx(
            choiceBoxClasses(size, isInvalid, disabled),
            'rounded-chip',
            disabled ? 'peer-checked:text-ink-subtle' : 'peer-checked:bg-primary peer-checked:text-white',
          )}
        >
          <svg viewBox="0 0 16 16" fill="none" className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'}>
            <path
              d="M3.5 8.5l3 3 6-7"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {label && <span className="min-w-0">{label}</span>}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';
